// Global search (Ctrl/⌘ K, or "/" where a page has no search box of its own): pages, children,
// adults, staff, and whatever modules add through the "console.search" slot (bus routes, buses).
// The lists come from the endpoints the pages already use, cached, fetched only while search is
// open: a school's roster is hundreds of rows, so matching in the browser is instant and needs no
// search index on the server.
import { useMemo, useState } from "react";
import { Avatar, CommandPalette, type IconName, type PaletteGroup } from "@vavr/ui";
import { navigate, slot, type SearchHit } from "../shared/index.ts";
import { useConsole, useGetMany } from "./data.tsx";
import { ROLE_LABEL } from "./common.tsx";

interface Source { id: string; label: string; path: string; items: (data: any) => SearchHit[] }
export interface SearchPage { id: string; label: string; icon: IconName; href: string }

const PER_GROUP = 6;
const words = (q: string) => q.toLowerCase().split(/\s+/).filter(Boolean);
const matches = (w: string[], ...text: (string | null | undefined)[]) => {
  const hay = text.filter(Boolean).join(" ").toLowerCase();
  return w.every((x) => hay.includes(x));
};

export function GlobalSearch({ open, onOpenChange, pages }: { open: boolean; onOpenChange: (o: boolean) => void; pages: SearchPage[] }) {
  const { site, base, can } = useConsole();
  const [q, setQ] = useState("");
  const depot = site.org_kind === "operator";
  const sources = useMemo<Source[]>(() => [
    ...(!depot && can("roster.read") ? [{
      id: "children", label: "Children", path: `${base}/children`,
      items: (d: { children: { id: string; first: string; last: string; room_name?: string | null; external_ref: string | null; status: string }[] }) =>
        d.children.filter((c) => c.status !== "archived").map((c) => ({ id: c.id, label: `${c.first} ${c.last}`, detail: [c.room_name, c.external_ref && `ID ${c.external_ref}`].filter(Boolean).join(" · ") || "Child", href: `/console/children/${c.id}` })),
    }] : []),
    ...(!depot && can("roster.write") ? [{
      id: "adults", label: "Adults", path: `${base}/adults`,
      items: (d: { adults: { id: string; first: string; last: string; email: string | null; phone: string | null }[] }) =>
        d.adults.map((a) => ({ id: a.id, label: `${a.first} ${a.last}`, detail: [a.email, a.phone].filter(Boolean).join(" · ") || "Adult", href: `/console/adults?open=${a.id}` })),
    }] : []),
    ...(can("staff.manage") ? [{
      id: "staff", label: "Staff", path: `${base}/members`,
      // One row per person, with every role they hold here.
      items: (d: { members: { id: string; account_id: string; name: string; email: string; role: string }[] }) => {
        const people = new Map<string, { id: string; name: string; email: string; roles: string[] }>();
        for (const m of d.members) {
          const p = people.get(m.account_id) ?? { id: m.account_id, name: m.name || m.email, email: m.email, roles: [] };
          p.roles.push(ROLE_LABEL[m.role] ?? m.role);
          people.set(m.account_id, p);
        }
        return [...people.values()].map((p) => ({ id: p.id, label: p.name, detail: `${p.roles.join(", ")} · ${p.email}`, href: "/console/staff" }));
      },
    }] : []),
    ...slot("console.search")
      .filter((s) => (!s.permission || can(s.permission)) && (!s.orgKind || s.orgKind === (depot ? "operator" : "school")))
      .map((s) => ({ id: s.id, label: s.label, path: s.path(site), items: s.items })),
  ], [base, can, depot, site]);
  const loaded = useGetMany(open ? sources.map((s) => s.path) : [], 60_000);
  const w = words(q);
  const groups: PaletteGroup[] = [];
  const go = (href: string) => () => navigate(href);
  const pageHits = pages.filter((p) => !w.length || matches(w, p.label));
  groups.push({ id: "pages", label: w.length ? "Pages" : "Go to", items: pageHits.slice(0, w.length ? PER_GROUP : 50).map((p) => ({ id: `page-${p.id}`, label: p.label, icon: p.icon, onSelect: go(p.href) })) });
  if (w.length) {
    sources.forEach((s, i) => {
      const data = loaded[i]?.data;
      if (!data) return;
      let hits: SearchHit[] = [];
      try { hits = s.items(data).filter((h) => matches(w, h.label, h.detail)); } catch { /* a module's odd response never breaks search */ }
      const person = s.id === "children" || s.id === "adults" || s.id === "staff";
      groups.push({
        id: s.id, label: hits.length > PER_GROUP ? `${s.label} · ${hits.length}` : s.label,
        items: hits.slice(0, PER_GROUP).map((h) => ({ id: `${s.id}-${h.id}`, label: h.label, detail: h.detail, icon: h.icon, lead: person ? <Avatar name={h.label} kind={s.id === "children" ? "child" : "adult"} size="sm" /> : undefined, onSelect: go(h.href) })),
      });
    });
  }
  const busy = !!w.length && loaded.some((e) => !e || (e.loading && !e.data));
  const mac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  return (
    <CommandPalette
      open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setQ(""); }} query={q} onQueryChange={setQ} groups={groups} busy={busy}
      placeholder={depot ? "Search pages, routes, buses and staff" : "Search children, adults, staff and pages"}
      empty={<>Nothing matches “{q.trim()}”.</>}
      footer={<><span><kbd>↑</kbd><kbd>↓</kbd> to move</span><span><kbd>↵</kbd> to open</span><span><kbd>{mac ? "⌘" : "Ctrl"}</kbd><kbd>K</kbd> anywhere</span></>}
    />
  );
}
